import React, { memo, useMemo } from 'react';
import dayjs, { Dayjs, UnitTypeLongPlural } from 'lib/dayjs';
import styled from '@emotion/styled';
import { isDemoChannel } from 'services/channels/demo';
import RangePickerCompat from 'bizComponents/demoChannel/RangePickerCompat';
import { DatePicker, RangePickerProps } from 'components/DatePicker';

function refine<T = any>(value: any, ...args: any[]): T {
  return typeof value === 'function' ? value(...args) : value;
}

/**
 * 时间区间值
 */
type Range = [Dayjs, Dayjs];

/**
 * 快捷选项
 */
export interface QuickAction {
  /**
   * 唯一键值
   */
  key: string;
  /**
   * 快捷选项显示标签
   */
  label: React.ReactNode;
  /**
   * 预设时间区间
   */
  range: Range | (() => Range);
}

export const simpleQuickActions: QuickAction[] = [
  {
    key: 'yesterday',
    label: '昨天',
    range: () => {
      return [dayjs().subtract(1, 'day').startOf('day'), dayjs().subtract(1, 'day').endOf('day')];
    },
  },
  {
    key: 'last7days',
    label: '近7天',
    range: () => {
      return [dayjs().subtract(7, 'day').startOf('day'), dayjs().subtract(1, 'day').endOf('day')];
    },
  },
  {
    key: 'last31days',
    label: '近31天',
    range: () => {
      return [dayjs().subtract(31, 'day').startOf('day'), dayjs().subtract(1, 'day').endOf('day')];
    },
  },
];

const PickerWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  > *:not(:last-child) {
    margin-right: 8px;
  }
  > .ant-picker:not(:last-child) {
    margin-right: 22px;
  }
`;

const PickerQuickAction = styled.span<{ active?: boolean; disabled?: boolean }>`
  display: inline-flex;
  font-size: 14px;
  color: ${(props) => (props.active ? 'var(--ant-primary-color)' : '#84868c')};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};

  &:not(:last-child) {
    margin-right: 8px;
  }
`;

/**
 * 判断是否是同一时间区间
 * @param a a 时间区间
 * @param b b 时间区间
 * @param unit 判断粒度
 */
export function isSameRange(a: Range, b: Range, unit: UnitTypeLongPlural = 'milliseconds') {
  return a[0].isSame(b[0], unit) && a[1].isSame(b[1], unit);
}

/**
 * 快捷选择区域位置
 */
export type QuickActionsPlacement = 'start' | 'end';

/**
 * 快捷时间区间选择器属性
 */
export type QuickRangePickerProps = RangePickerProps & {
  /**
   * 快捷选择区域位置
   */
  quickActionsPlacement?: QuickActionsPlacement;
  /**
   * 快捷选项
   */
  quickActions?: QuickAction[];
  /**
   * 快捷选项点击回调
   */
  onQuickActionClick?: (action: QuickAction) => void;
  Picker?: typeof DatePicker.RangePicker;
};

/**
 * 快捷时间区间选择器
 * @param props 快捷时间区间选择器属性
 */
export function QuickRangePicker(props: QuickRangePickerProps) {
  const {
    quickActionsPlacement = 'start',
    quickActions = simpleQuickActions,
    onQuickActionClick,
    format,
    value,
    onChange,
    className,
    style,
    disabled,
    Picker = DatePicker.RangePicker,
    ...otherProps
  } = props;

  // FIXME: `RangePickerProps` 类型定义了 `showTime` 但类型推导后该类型因为未知原因被移除了，
  //        此处先手动定义了 `showTime` 类型
  const { showTime } = props as { showTime?: boolean };
  const unit: UnitTypeLongPlural = showTime ? 'seconds' : 'days';
  const formatString = useMemo(() => {
    if (format === undefined) {
      const format = showTime ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD';

      return [format, format] as [string, string];
    }

    const formats = Array.isArray(format) ? format : [format, format];

    return formats.map((format) => {
      return refine<string>(format);
    }) as [string, string];
  }, [format, showTime]);

  const picker = (
    <Picker
      {...otherProps}
      disabled={disabled}
      format={formatString}
      value={value}
      onChange={onChange}
    />
  );

  const actions = quickActions.map((action) => {
    const { key, label, range } = action;

    const actionRange = refine<Range>(range, unit);
    const active =
      value && value[0] && value[1] ? isSameRange(value as Range, actionRange, unit) : false;
    function handleActive() {
      if (disabled) {
        return;
      }

      if (!active) {
        onChange!(actionRange, formatString);
      }

      if (onQuickActionClick) {
        onQuickActionClick(action);
      }
    }

    return (
      <PickerQuickAction
        key={key}
        active={active}
        disabled={disabled === true}
        onClick={onChange && handleActive}
      >
        {label}
      </PickerQuickAction>
    );
  });

  return (
    <PickerWrapper className={className} style={style}>
      {quickActionsPlacement === 'end' && picker}
      <div>{actions}</div>
      {quickActionsPlacement === 'start' && picker}
    </PickerWrapper>
  );
}

/**
 * 演示用渠道包大部分接口需要限制在特殊日期内才能获取有效数据，
 * 因此使用HOC进行一层特殊封装，尽量不暴露在上层调用。
 * @思睿 @haofeng.zhao @褚善博
 * @param props
 * @returns
 */
const QuickRangePickerForDemo = (props: QuickRangePickerProps) => {
  return <QuickRangePicker {...props} Picker={RangePickerCompat as any} quickActions={[]} />;
};

const QuickRangePickerCompat = isDemoChannel ? memo(QuickRangePickerForDemo) : QuickRangePicker;

export default QuickRangePickerCompat;
